import { useEffect } from "react";

// Puts a page's search-engine and social-network information into <head> while the page is shown, and puts everything back when it is left:
// the title, the description, the canonical address, the Open Graph / Twitter cards and the structured data (JSON-LD).
// Search engines run the page's JavaScript and read these; the static pages the website is built with (see "export_blog_seo") carry the same tags
// for the networks that do not.
function head() { return document.head; }

function setMeta(changes, attribute, key, content) {
  if (content === undefined || content === null || content === "") return;
  let element = head().querySelector(`meta[${attribute}="${key}"]`);
  const previous = element ? element.getAttribute("content") : null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    head().appendChild(element);
  }
  element.setAttribute("content", String(content));
  changes.push(() => { if (previous === null) element.remove(); else element.setAttribute("content", previous); });
}

export default function useSeo({ title, description, canonical, image, type = "website", robots, keywords, published, modified, section, structuredData } = {}) {
  const dataKey = JSON.stringify(structuredData || []);
  useEffect(() => {
    if (!title) return undefined;
    const changes = [];
    const previousTitle = document.title;
    document.title = title;
    changes.push(() => { document.title = previousTitle; });

    setMeta(changes, "name", "description", description);
    setMeta(changes, "name", "robots", robots);
    setMeta(changes, "name", "keywords", keywords);
    setMeta(changes, "property", "og:type", type);
    setMeta(changes, "property", "og:title", title);
    setMeta(changes, "property", "og:description", description);
    setMeta(changes, "property", "og:url", canonical);
    setMeta(changes, "property", "og:image", image);
    setMeta(changes, "property", "article:published_time", published);
    setMeta(changes, "property", "article:modified_time", modified);
    setMeta(changes, "property", "article:section", section);
    setMeta(changes, "name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta(changes, "name", "twitter:title", title);
    setMeta(changes, "name", "twitter:description", description);
    setMeta(changes, "name", "twitter:image", image);

    if (canonical) {
      let link = head().querySelector('link[rel="canonical"]');
      const previous = link ? link.getAttribute("href") : null;
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); head().appendChild(link); }
      link.setAttribute("href", canonical);
      changes.push(() => { if (previous === null) link.remove(); else link.setAttribute("href", previous); });
    }
    JSON.parse(dataKey).forEach((block) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "1");
      script.text = JSON.stringify(block);
      head().appendChild(script);
      changes.push(() => script.remove());
    });
    return () => changes.reverse().forEach((undo) => undo());
  }, [title, description, canonical, image, type, robots, keywords, published, modified, section, dataKey]);
}
